import { useMemo } from 'react'
import { diffLines, createPatch } from 'diff'

export interface DiffComputationResult {
  additions: number
  deletions: number
  diffText: string
  changes: Array<{ value: string; added?: boolean; removed?: boolean }>
  /** >=50% non-empty line removal - informational warning */
  isDestructive: boolean
  /** >=80% non-empty line removal - major rewrite warning above Accept CTA */
  isMajorDeletion: boolean
  deletionPercentage: number
  normalizedOriginal: string
  normalizedProposed: string
}

/**
 * Custom hook to compute diff between two text contents.
 * Handles normalization, filtering, and statistics calculation.
 */
export function useDiffComputation(
  original: string,
  proposed: string,
  filename: string = 'document.md'
): DiffComputationResult {
  return useMemo(() => {
    // Helper to check if a line is whitespace-only
    const isWhitespaceOnly = (line: string) => !line || line.trim().length === 0

    const diffChanges = diffLines(original, proposed)
    
    // Filter out whitespace-only changes and count only meaningful changes
    let adds = 0
    let dels = 0
    const filteredChanges: typeof diffChanges = []
    
    diffChanges.forEach(change => {
      if (change.added) {
        // Split into lines and count only non-whitespace lines
        const lines = change.value.split('\n')
        const nonWhitespaceLines = lines.filter(line => !isWhitespaceOnly(line))
        const actualAdds = nonWhitespaceLines.length
        if (actualAdds > 0) {
          adds += actualAdds
          filteredChanges.push(change)
        }
      } else if (change.removed) {
        // Split into lines and count only non-whitespace lines
        const lines = change.value.split('\n')
        const nonWhitespaceLines = lines.filter(line => !isWhitespaceOnly(line))
        const actualDels = nonWhitespaceLines.length
        if (actualDels > 0) {
          dels += actualDels
          filteredChanges.push(change)
        }
      } else {
        // Unchanged sections - keep as is
        filteredChanges.push(change)
      }
    })
    
    // Normalize content to ignore whitespace-only differences
    // This prevents empty/whitespace-only lines from showing as changes
    const normalizeContent = (content: string) => {
      const lines = content.split('\n')
      const normalized: string[] = []
      let lastWasEmpty = false
      
      for (const line of lines) {
        const trimmed = line.trimEnd()
        const isEmpty = trimmed.length === 0
        
        if (isEmpty) {
          // Collapse consecutive empty lines into single empty line
          // This prevents empty line additions/removals from showing as changes
          if (!lastWasEmpty) {
            normalized.push('')
          }
          lastWasEmpty = true
        } else {
          normalized.push(trimmed)
          lastWasEmpty = false
        }
      }
      
      // Remove trailing empty lines (they're not meaningful)
      while (normalized.length > 0 && normalized[normalized.length - 1] === '') {
        normalized.pop()
      }
      
      return normalized.join('\n')
    }
    
    const normalizedOriginal = normalizeContent(original)
    const normalizedProposed = normalizeContent(proposed)
    
    // Post-process patch to remove hunks that only contain empty lines
    // Parse the patch and filter out empty-line-only changes
    const filterEmptyLineHunks = (patch: string): string => {
      const lines = patch.split('\n')
      const filtered: string[] = []
      let inHunk = false
      let hunkStart = -1
      let hunkLines: string[] = []
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        
        // Detect hunk start
        if (line.startsWith('@@')) {
          // Process previous hunk if it exists
          if (inHunk && hunkStart >= 0) {
            // Check if hunk only contains empty lines
            const hasNonEmptyChanges = hunkLines.some(l => {
              if (l.startsWith('+') || l.startsWith('-')) {
                const content = l.substring(1).trim()
                return content.length > 0
              }
              return false
            })
            
            // Only include hunk if it has non-empty changes
            if (hasNonEmptyChanges) {
              filtered.push(lines[hunkStart])
              filtered.push(...hunkLines)
            }
          }
          
          // Start new hunk
          hunkStart = i
          hunkLines = []
          inHunk = true
        } else if (inHunk) {
          // Collect hunk lines
          if (line.startsWith('+') || line.startsWith('-') || line.startsWith(' ')) {
            hunkLines.push(line)
          } else {
            // End of hunk
            const hasNonEmptyChanges = hunkLines.some(l => {
              if (l.startsWith('+') || l.startsWith('-')) {
                const content = l.substring(1).trim()
                return content.length > 0
              }
              return false
            })
            
            if (hasNonEmptyChanges) {
              filtered.push(lines[hunkStart])
              filtered.push(...hunkLines)
            }
            
            inHunk = false
            hunkStart = -1
            hunkLines = []
            
            // Add non-hunk lines (headers, etc.)
            if (!line.startsWith('@@')) {
              filtered.push(line)
            }
          }
        } else {
          // Non-hunk lines (headers, etc.)
          filtered.push(line)
        }
      }
      
      // Process final hunk if exists
      if (inHunk && hunkStart >= 0) {
        const hasNonEmptyChanges = hunkLines.some(l => {
          if (l.startsWith('+') || l.startsWith('-')) {
            const content = l.substring(1).trim()
            return content.length > 0
          }
          return false
        })
        
        if (hasNonEmptyChanges) {
          filtered.push(lines[hunkStart])
          filtered.push(...hunkLines)
        }
      }
      
      return filtered.join('\n')
    }
    
    // Generate unified diff patch using normalized content
    // This creates the proper format that @git-diff-view expects
    let patch = createPatch(
      filename,
      normalizedOriginal,
      normalizedProposed,
      'Original',
      'Proposed'
    )
    
    // Filter out hunks that only contain empty line changes
    patch = filterEmptyLineHunks(patch)
    
    console.log('[DiffComputation] Generated diff:', { 
      additions: adds, 
      deletions: dels, 
      patchLength: patch.length,
      originalLength: original.length,
      proposedLength: proposed.length
    })
    
    // Tiered deletion warnings aligned with backend guard (50% soft, 80% hard)
    const originalNonEmptyLines = original.split('\n').filter((l) => l.trim().length > 0).length
    const deletionPercentage =
      originalNonEmptyLines > 0 ? (dels / originalNonEmptyLines) * 100 : 0
    const destructive = deletionPercentage > 50
    const majorDeletion = deletionPercentage > 80

    return {
      additions: adds,
      deletions: dels,
      diffText: patch,
      changes: filteredChanges,
      isDestructive: destructive,
      isMajorDeletion: majorDeletion,
      deletionPercentage,
      normalizedOriginal: normalizedOriginal,
      normalizedProposed: normalizedProposed
    }
  }, [original, proposed, filename])
}

